import { useTranslation } from "react-i18next";
import { useLangFont } from "../../../hooks/useLangFont";

const snsLinks = [
  {
    icon: "ri-instagram-line",
    label: "Instagram",
    href: "https://www.instagram.com/zoshinjp?igsh=MTFvaDYxdXd6bWYzZQ%3D%3D&utm_source=qr",
    color: "hover:text-[#E1306C]",
  },
  {
    icon: "ri-tiktok-line",
    label: "TikTok",
    href: "https://www.tiktok.com/@zoshin41?_r=1&_t=ZS-951gQNSs6x3",
    color: "hover:text-white",
  },
  {
    icon: "ri-twitter-x-line",
    label: "X",
    href: "https://x.com/zoshinjp?s=21",
    color: "hover:text-white",
  },
  {
    icon: "ri-facebook-circle-line",
    label: "Facebook",
    href: "https://www.facebook.com/share/1KC5Jk7nG7/?mibextid=wwXIfr",
    color: "hover:text-[#1877F2]",
  },
];

const footerLinkKeys = [
  { key: "footer_link_0", href: "#pricing" },
  { key: "footer_link_1", href: "#pricing" },
  { key: "footer_link_2", href: "#gallery" },
  { key: "footer_link_3", href: "#b2b" },
  { key: "footer_link_4", href: "#contact" },
];

const contactItems = [
  {
    icon: "ri-chat-smile-2-line",
    nameKey: "contact_ch_0_name",
    iconColor: "text-[#06C755]",
    iconBg: "bg-[#06C755]/10",
    qr: "https://static.readdy.ai/image/09909eceab93e731ba4fd92f69d7cdad/a30d475fe537245c8258270cd6281102.png",
    email: null,
  },
  {
    icon: "ri-wechat-line",
    nameKey: "contact_ch_1_name",
    iconColor: "text-[#2aae67]",
    iconBg: "bg-[#2aae67]/10",
    qr: "https://static.readdy.ai/image/09909eceab93e731ba4fd92f69d7cdad/e52510f6195ea590e26e49fdc56eb2b2.png",
    email: null,
  },
  {
    icon: "ri-mail-send-line",
    nameKey: "contact_ch_2_name",
    iconColor: "text-[#C9A84C]",
    iconBg: "bg-[#C9A84C]/10",
    qr: null,
    email: "zoshinjp@worldlink.jp",
  },
];

export default function Footer() {
  const { t } = useTranslation();
  const { heading, body } = useLangFont();

  const handleNav = (e: React.MouseEvent<HTMLAnchorElement>, href: string) => {
    e.preventDefault();
    const target = document.querySelector(href);
    if (target) target.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <footer className="bg-[#111923] w-full">
      {/* Top band */}
      <div className="bg-[#C9A84C]/10 border-t border-b border-[#C9A84C]/20 py-5 px-6">
        <div className="max-w-7xl mx-auto flex flex-wrap justify-center gap-6 items-center text-center">
          <span className="flex items-center gap-2 text-white/60 text-xs" style={{ fontFamily: body }}>
            <i className="ri-truck-line text-[#C9A84C] w-4 h-4 flex items-center justify-center" />
            {t("footer_band_delivery")}
          </span>
          <span className="text-[#C9A84C]/30 hidden sm:block">·</span>
          <span className="flex items-center gap-2 text-white/60 text-xs" style={{ fontFamily: body }}>
            <i className="ri-timer-flash-line text-[#C9A84C] w-4 h-4 flex items-center justify-center" />
            {t("footer_band_speed")}
          </span>
          <span className="text-[#C9A84C]/30 hidden sm:block">·</span>
          <span className="flex items-center gap-2 text-white/60 text-xs" style={{ fontFamily: body }}>
            <i className="ri-calendar-line text-[#C9A84C] w-4 h-4 flex items-center justify-center" />
            {t("footer_band_hours")}
          </span>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-16">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
          {/* Brand */}
          <div className="md:col-span-1">
            <div className="flex items-center gap-3 mb-5">
              <img
                src="https://static.readdy.ai/image/09909eceab93e731ba4fd92f69d7cdad/bd37e4785bae3db4f1c4b94805dc789e.jpeg"
                alt="蔵新ロゴ"
                className="h-12 w-12 rounded-full object-cover flex-shrink-0"
              />
              <div className="flex flex-col leading-tight">
                <span
                  className="text-[#C9A84C] font-bold text-2xl tracking-widest leading-tight"
                  style={{ fontFamily: heading }}
                >
                  蔵新
                </span>
                <span
                  className="text-white/65 leading-tight"
                  style={{ fontFamily: body, fontSize: "0.68rem", letterSpacing: "0.12em" }}
                >
                  高級バッグ修復専門
                </span>
                <span
                  className="text-white/35 uppercase leading-tight"
                  style={{ fontFamily: "'Montserrat', sans-serif", fontSize: "0.55rem", letterSpacing: "0.1em" }}
                >
                  Luxury Bag Restoration Studio
                </span>
              </div>
            </div>
            <p
              className="text-white/50 text-xs leading-relaxed max-w-xs"
              style={{ fontFamily: body }}
            >
              {t("footer_desc")}
            </p>
            {/* SNS Links */}
            <div className="mt-6">
              <p className="text-white/40 text-xs uppercase tracking-widest mb-3" style={{ fontFamily: "'Montserrat', sans-serif" }}>
                {t("footer_sns_title")}
              </p>
              <div className="flex items-center gap-3">
                {snsLinks.map((sns) => (
                  <a
                    key={sns.label}
                    href={sns.href}
                    target="_blank"
                    rel="noopener noreferrer nofollow"
                    aria-label={sns.label}
                    className={`w-9 h-9 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center text-white/40 ${sns.color} transition-all duration-300 hover:bg-white/10 hover:border-white/20 cursor-pointer`}
                  >
                    <i className={`${sns.icon} text-sm w-4 h-4 flex items-center justify-center`} />
                  </a>
                ))}
              </div>
            </div>
          </div>

          {/* Navigation */}
          <div>
            <h4 className="text-white font-bold text-sm tracking-widest mb-5 uppercase" style={{ fontFamily: "'Montserrat', sans-serif" }}>
              {t("footer_services_title")}
            </h4>
            <ul className="flex flex-col gap-3">
              {footerLinkKeys.map((link) => (
                <li key={link.key}>
                  <a
                    href={link.href}
                    onClick={(e) => handleNav(e, link.href)}
                    className="text-white/50 hover:text-[#C9A84C] text-sm transition-colors duration-300 cursor-pointer"
                    style={{ fontFamily: body }}
                  >
                    {t(link.key)}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="text-white font-bold text-sm tracking-widest mb-5 uppercase" style={{ fontFamily: "'Montserrat', sans-serif" }}>
              {t("footer_contact_title")}
            </h4>
            <ul className="flex flex-col gap-5">
              {contactItems.map((ch) => (
                <li key={ch.nameKey} className="flex items-center gap-3">
                  <div className={`w-9 h-9 rounded-lg ${ch.iconBg} flex items-center justify-center flex-shrink-0`}>
                    <i className={`${ch.icon} ${ch.iconColor} text-base w-4 h-4 flex items-center justify-center`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <span className="text-white/75 text-sm block leading-snug" style={{ fontFamily: body }}>
                      {t(ch.nameKey)}
                    </span>
                    {ch.email && (
                      <a
                        href={`mailto:${ch.email}`}
                        className="text-white/40 hover:text-[#C9A84C] text-xs transition-colors duration-200 cursor-pointer truncate block"
                        style={{ fontFamily: "'Montserrat', sans-serif", letterSpacing: "0.03em" }}
                      >
                        {ch.email}
                      </a>
                    )}
                  </div>
                  {ch.qr && (
                    <div className="w-14 h-14 rounded-lg overflow-hidden border border-white/10 flex-shrink-0">
                      <img
                        src={ch.qr}
                        alt="QRコード"
                        className="w-full h-full object-cover"
                      />
                    </div>
                  )}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom */}
      <div className="border-t border-white/5 px-6 py-5">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-2">
          <p className="text-white/30 text-xs" style={{ fontFamily: body }}>
            {t("footer_copyright")}
          </p>
          <div className="flex items-center gap-4">
            <a href="#" rel="nofollow" className="text-white/30 hover:text-white/60 text-xs transition-colors duration-300 cursor-pointer" style={{ fontFamily: body }}>
              {t("footer_privacy")}
            </a>
            <span className="text-white/20">|</span>
            <a href="#" rel="nofollow" className="text-white/30 hover:text-white/60 text-xs transition-colors duration-300 cursor-pointer" style={{ fontFamily: body }}>
              {t("footer_law")}
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
}
